import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { EmptyState } from '../components/shared/EmptyState';
import { frameworksAPI } from '../lib/api';
import { Folder, Plus, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const PRESET_FRAMEWORKS = [
  { key: 'soc2', name: 'SOC 2 Type II', desc: 'Service Organization Control 2 - Trust Services Criteria' },
  { key: 'iso27001', name: 'ISO 27001', desc: 'Information Security Management System' },
  { key: 'gdpr', name: 'GDPR', desc: 'General Data Protection Regulation - EU Privacy' },
  { key: 'hipaa', name: 'HIPAA', desc: 'Health Insurance Portability and Accountability Act' },
];

export default function FrameworksPage() {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [seedingFramework, setSeedingFramework] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    version: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchFrameworks();
  }, []);

  const fetchFrameworks = async () => {
    try {
      const response = await frameworksAPI.getAll();
      setFrameworks(response.data);
    } catch (error) {
      toast.error('Failed to load frameworks');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedFramework = async (frameworkKey) => {
    setSeedingFramework(frameworkKey);
    try {
      const endpoints = {
        soc2: '/seed/soc2',
        iso27001: '/seed/iso27001',
        gdpr: '/seed/gdpr',
        hipaa: '/seed/hipaa',
      };
      
      const response = await frameworksAPI.seedSOC2.__proto__.constructor.prototype.call.bind(
        frameworksAPI.seedSOC2,
        { post: (url) => frameworksAPI.seedSOC2.__proto__.constructor.call({ baseURL: '' }, url) }
      );
      
      // Use direct API call
      const { data } = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoints[frameworkKey]}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('grc_token')}`,
          'Content-Type': 'application/json'
        }
      }).then(res => res.json().then(data => ({ data })));
      
      toast.success(data.message);
      fetchFrameworks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load framework');
    } finally {
      setSeedingFramework(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await frameworksAPI.create(formData);
      toast.success('Framework created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', version: '', description: '' });
      fetchFrameworks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create framework');
    } finally {
      setSubmitting(false);
    }
  };

  const isFrameworkLoaded = (name) => {
    return frameworks.some(fw => fw.name.toLowerCase().includes(name.toLowerCase()));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Frameworks" 
        description="Compliance framework library"
        actions={
          <Button
            data-testid="add-framework-btn"
            onClick={() => setIsDialogOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Custom Framework
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Preset Frameworks */}
        <div className="mb-8">
          <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
            Load Compliance Frameworks
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRESET_FRAMEWORKS.map((preset) => {
              const loaded = isFrameworkLoaded(preset.name.split(' ')[0]);
              return (
                <div 
                  key={preset.key}
                  className={`bg-white border rounded-sm p-4 ${
                    loaded ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'
                  }`}
                >
                  <h4 className="font-semibold text-slate-900 mb-1">{preset.name}</h4>
                  <p className="text-xs text-slate-500 mb-3">{preset.desc}</p>
                  <Button
                    data-testid={`load-${preset.key}-btn`}
                    size="sm"
                    variant={loaded ? 'outline' : 'default'}
                    disabled={loaded || seedingFramework === preset.key}
                    onClick={() => handleSeedFramework(preset.key)}
                    className={`w-full rounded-sm ${loaded ? 'text-emerald-600 border-emerald-300' : ''}`}
                  >
                    {seedingFramework === preset.key ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : loaded ? (
                      '✓ Loaded'
                    ) : (
                      'Load Framework'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Loaded Frameworks */}
        <h3 className="font-secondary font-semibold text-lg text-slate-900 mb-4">
          Active Frameworks ({frameworks.length})
        </h3>
        
        {frameworks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {frameworks.map((framework) => (
              <div 
                key={framework.id}
                className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
                    <Shield size={24} className="text-blue-600" />
                  </div>
                  <span className="badge badge-neutral font-mono">v{framework.version}</span>
                </div>

                <h3 className="font-secondary font-bold text-xl text-slate-900 mb-2">
                  {framework.name}
                </h3>
                
                {framework.description && (
                  <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                    {framework.description}
                  </p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{framework.controls_count}</p>
                    <p className="text-xs text-slate-500">controls</p>
                  </div>
                  <Link 
                    to={`/controls?framework=${framework.id}`}
                    data-testid={`view-framework-${framework.id}`}
                    className="flex items-center gap-1 text-sm text-blue-600 font-medium group-hover:underline"
                  >
                    View Controls
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Folder}
            title="No frameworks loaded"
            description="Load a compliance framework from the options above to get started."
          />
        )}
      </div>

      {/* Add Custom Framework Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-secondary">Create Custom Framework</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Framework Name *</Label>
              <Input
                data-testid="framework-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., PCI DSS"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Version *</Label>
              <Input
                data-testid="framework-version-input"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="e.g., 4.0"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                data-testid="framework-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the framework..."
                rows={3}
                className="mt-1.5 rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-framework-btn"
                disabled={submitting}
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                {submitting ? 'Creating...' : 'Create Framework'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
